import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS, submitForm } from '../config/api';

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const getDaysInMonth = (month, year) => {
  if (!month || !year) return 31;
  return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear + 1];
};

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  name: '',
  email: '',
  phone: '',
  month: '',
  day: '',
  year: '',
  date: '',
  time: '',
  message: '',
};

const BOOKING_STEPS = [
  { field: 'firstName', emoji: '👋', label: "What's your first name?", placeholder: 'First name', type: 'text', required: true },
  { field: 'lastName', emoji: '✨', label: "What's your last name?", placeholder: 'Last name', type: 'text', required: true },
  { field: 'email', emoji: '📧', label: "What's your email?", placeholder: 'you@example.com', type: 'email', required: true },
  { field: 'phone', emoji: '📱', label: "What's your phone number?", placeholder: '(818) 555-0123', type: 'tel', required: true },
  { field: 'date', emoji: '📅', label: 'Preferred consultation date', placeholder: '', type: 'dateDropdowns', required: true },
  { field: 'time', emoji: '⏰', label: 'Preferred consultation time', placeholder: '', type: 'select', required: true },
  { field: 'message', emoji: '💬', label: 'Anything else you want to share? (Optional)', placeholder: 'Tell us about your project...', type: 'textarea', required: false },
];

/**
 * Multi-step free consultation booking form.
 * @param {{ onSuccess?: () => void }} props
 */
export default function BookingForm({ onSuccess }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [bookingStep, setBookingStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const stepInputRef = useRef(null);

  useEffect(() => {
    stepInputRef.current?.focus();
  }, [bookingStep]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'month' || name === 'year') {
        const maxDay = getDaysInMonth(next.month, next.year);
        if (next.day && parseInt(next.day, 10) > maxDay) {
          next.day = '';
        }
      }

      return next;
    });
  };

  const getFormattedDate = () => {
    const { month, day, year } = formData;
    if (!month || !day || !year) return formData.date || '';
    return `${year}-${month}-${day.padStart(2, '0')}`;
  };

  const isDateSelectionValid = () => {
    const { month, day, year } = formData;
    if (!month || !day || !year) return false;

    const daysInMonth = getDaysInMonth(month, year);
    if (parseInt(day, 10) > daysInMonth) return false;

    const selectedDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  };

  const isCurrentStepValid = () => {
    const step = BOOKING_STEPS[bookingStep];
    if (!step.required) return true;

    if (step.type === 'dateDropdowns') {
      return isDateSelectionValid();
    }

    const value = formData[step.field]?.trim();
    if (!value) return false;
    if (step.type === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    return true;
  };

  const handleBookingNext = () => {
    if (!isCurrentStepValid()) return;
    if (bookingStep < BOOKING_STEPS.length - 1) {
      setBookingStep((prev) => prev + 1);
    }
  };

  const handleBookingBack = () => {
    if (bookingStep > 0) {
      setBookingStep((prev) => prev - 1);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    const fullName = `${formData.firstName} ${formData.lastName}`.trim() || formData.name.trim();

    try {
      const { ok, data } = await submitForm(API_ENDPOINTS.CONSULTATION, {
        type: 'consultation',
        name: fullName,
        email: formData.email,
        phone: formData.phone,
        date: getFormattedDate(),
        time: formData.time,
        message: formData.message,
      });

      if (ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Consultation request submitted successfully! We will contact you soon.',
        });
        setFormData(INITIAL_FORM);
        setBookingStep(0);
        setTimeout(() => {
          setSubmitStatus({ type: '', message: '' });
          onSuccess?.();
        }, 2000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Failed to submit consultation request. Please try again.',
        });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDateDropdownFields = (selectClassName, monthRef = null) => {
    const maxDays = getDaysInMonth(formData.month, formData.year);
    const dayOptions = Array.from({ length: maxDays }, (_, i) => i + 1);
    const yearOptions = getYearOptions();
    const labelStyle = {
      fontFamily: "'Poppins', sans-serif",
      color: '#292929',
      fontSize: '0.95rem',
    };
    const selectStyle = { fontFamily: "'Poppins', sans-serif", color: '#292929' };

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block mb-2" style={labelStyle}>Month *</label>
          <select
            ref={monthRef}
            name="month"
            value={formData.month}
            onChange={handleInputChange}
            required
            className={selectClassName}
            style={selectStyle}
          >
            <option value="">Month</option>
            {MONTH_OPTIONS.map((month) => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2" style={labelStyle}>Day *</label>
          <select
            name="day"
            value={formData.day}
            onChange={handleInputChange}
            required
            className={selectClassName}
            style={selectStyle}
          >
            <option value="">Day</option>
            {dayOptions.map((day) => (
              <option key={day} value={String(day)}>{day}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2" style={labelStyle}>Year *</label>
          <select
            name="year"
            value={formData.year}
            onChange={handleInputChange}
            required
            className={selectClassName}
            style={selectStyle}
          >
            <option value="">Year</option>
            {yearOptions.map((year) => (
              <option key={year} value={String(year)}>{year}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const currentStep = BOOKING_STEPS[bookingStep];
  const isLastStep = bookingStep === BOOKING_STEPS.length - 1;
  const isTextarea = currentStep.type === 'textarea';
  const inputClassName =
    'booking-fun-input relative z-0 w-full py-4 pr-5 text-base md:text-lg rounded-2xl bg-white/90 backdrop-blur-sm border-2 border-white/60 focus:outline-none focus:border-[#ff7a59] focus:bg-white transition-all duration-300 shadow-md hover:shadow-lg hover:border-white';
  const inputStyle = {
    fontFamily: "'Poppins', sans-serif",
    color: '#292929',
    paddingLeft: '3.75rem',
  };

  const handleStepKeyDown = (e) => {
    if (e.key === 'Enter' && !isLastStep) {
      e.preventDefault();
      handleBookingNext();
    }
  };

  const renderStepInput = () => {
    const emojiClass = isTextarea
      ? 'absolute left-4 top-4 z-10 text-2xl pointer-events-none booking-step-emoji'
      : 'absolute left-4 top-1/2 -translate-y-1/2 z-10 text-2xl pointer-events-none booking-step-emoji';

    const dateSelectClassName =
      'booking-fun-input relative z-0 w-full py-4 px-4 text-base md:text-lg rounded-2xl bg-white/90 backdrop-blur-sm border-2 border-white/60 focus:outline-none focus:border-[#ff7a59] focus:bg-white transition-all duration-300 shadow-md hover:shadow-lg hover:border-white';

    const inputWrapper = (input) => (
      <div key={bookingStep} className="booking-input-pop relative group">
        <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#ff7a59]/40 via-[#accbff]/60 to-[#ff7a59]/40 opacity-0 group-focus-within:opacity-100 blur-sm transition-opacity duration-300 pointer-events-none z-0" />
        <div className="relative z-0">
          {input}
          <span className={emojiClass} aria-hidden="true">{currentStep.emoji}</span>
        </div>
      </div>
    );

    if (currentStep.type === 'dateDropdowns') {
      return (
        <div key={bookingStep} className="booking-input-pop">
          <div className="text-center text-3xl mb-4" aria-hidden="true">{currentStep.emoji}</div>
          {renderDateDropdownFields(dateSelectClassName, stepInputRef)}
        </div>
      );
    }

    if (currentStep.type === 'select') {
      return inputWrapper(
        <select
          ref={stepInputRef}
          name={currentStep.field}
          value={formData[currentStep.field]}
          onChange={handleInputChange}
          onKeyDown={handleStepKeyDown}
          required={currentStep.required}
          className={inputClassName}
          style={inputStyle}
        >
          <option value="">Pick a time...</option>
          <option value="9am">9:00 AM</option>
          <option value="10am">10:00 AM</option>
          <option value="11am">11:00 AM</option>
          <option value="12pm">12:00 PM</option>
          <option value="1pm">1:00 PM</option>
          <option value="2pm">2:00 PM</option>
          <option value="3pm">3:00 PM</option>
          <option value="4pm">4:00 PM</option>
          <option value="5pm">5:00 PM</option>
        </select>
      );
    }

    if (currentStep.type === 'textarea') {
      return inputWrapper(
        <textarea
          ref={stepInputRef}
          name={currentStep.field}
          value={formData[currentStep.field]}
          onChange={handleInputChange}
          rows="4"
          className={`${inputClassName} resize-none`}
          style={{ ...inputStyle, paddingTop: '1rem' }}
          placeholder={currentStep.placeholder}
        />
      );
    }

    return inputWrapper(
      <input
        ref={stepInputRef}
        type={currentStep.type}
        name={currentStep.field}
        value={formData[currentStep.field]}
        onChange={handleInputChange}
        onKeyDown={handleStepKeyDown}
        required={currentStep.required}
        className={inputClassName}
        style={inputStyle}
        placeholder={currentStep.placeholder}
      />
    );
  };

  return (
    <>
      {submitStatus.message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {submitStatus.message}
        </div>
      )}

      <div className="mb-8">
        <div className="flex justify-center gap-2 mb-6">
          {BOOKING_STEPS.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === bookingStep
                  ? 'w-8 bg-[#ff7a59]'
                  : index < bookingStep
                    ? 'w-2 bg-[#ff7a59]/60'
                    : 'w-2 bg-white/40'
              }`}
            />
          ))}
        </div>
        <p
          className="text-center text-xs uppercase tracking-[0.2em] mb-3"
          style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59', fontWeight: 600 }}
        >
          Step {bookingStep + 1} of {BOOKING_STEPS.length}
        </p>
        <h3
          className="text-xl md:text-2xl font-bold text-center mb-6"
          style={{
            fontFamily: "'Poppins', sans-serif",
            color: '#292929',
          }}
        >
          {currentStep.label}
        </h3>
      </div>

      <form
        className="space-y-6"
        onSubmit={
          isLastStep
            ? handleConsultationSubmit
            : (e) => {
                e.preventDefault();
                handleBookingNext();
              }
        }
      >
        <div>{renderStepInput()}</div>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
          <button
            type="button"
            onClick={handleBookingBack}
            disabled={bookingStep === 0}
            className="px-6 py-3 rounded-full border border-white/40 text-gray-700 font-medium transition-all duration-300 hover:border-[#ff7a59] hover:text-[#ff7a59] disabled:opacity-0 disabled:pointer-events-none"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Back
          </button>

          {isLastStep ? (
            <button
              type="submit"
              disabled={isSubmitting}
              className="group ml-auto w-[180px] md:w-[200px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#ff7a59',
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              <span className="relative z-10 text-sm md:text-base">
                {isSubmitting ? 'SUBMITTING...' : 'BOOK CONSULTATION'}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!isCurrentStepValid()}
              className="group ml-auto w-[140px] md:w-[160px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#ff7a59',
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              <span className="relative z-10 text-sm md:text-base">Next</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </button>
          )}
        </div>
      </form>
    </>
  );
}
